import { HEARING_NAVIGATION, HearingNavigationService } from "../hearing-detail/hearing-navigation.service";
import { HearingCycleView } from "../navigation/phms-detail-navigation";
import { CommonModule } from "@angular/common";
import { Component, ComponentRef, ElementRef, EventEmitter, Output, ViewContainerRef, effect, inject, input, signal, untracked, viewChild } from "@angular/core";
import { read } from "@template-ui/helpers";
import { TabConfig } from "@template-ui/types";

/**
 * Hosts the active section editor of a hearing phase. Selection follows the hearing navigation
 * context (the phase page's tabs, #1530); the section heading lives in the panel as the focus
 * target after a switch. Retains the active editor when its input configuration changes.
 */
@Component({
  selector: "phms-hearing-task-workspace",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="task-workspace" role="group" [attr.aria-label]="navigationLabel()">
      <section class="task-content" [attr.aria-label]="activeLabel()" [attr.data-task]="activeLabel()">
        <header class="task-heading">
          <h2 #heading tabindex="-1" class="task-heading__title visually-hidden">{{ activeLabel() }}</h2>
          <ng-content select="[task-actions]"></ng-content>
        </header>
        <ng-container #content></ng-container>
      </section>
    </div>
  `,
  styleUrl: "./hearing-task-workspace.component.scss",
})
export class HearingTaskWorkspaceComponent {
  readonly tabConfig = input<TabConfig[]>([]);
  readonly hearingPhase = input<HearingCycleView | null>(null);
  readonly navigation = inject(HearingNavigationService);
  readonly navigationLabel = input("Sections");
  @Output() readonly taskSelected = new EventEmitter<number>();
  readonly read = read;
  private readonly selected = signal(0);
  private readonly content = viewChild("content", { read: ViewContainerRef });
  private readonly heading = viewChild<ElementRef<HTMLElement>>("heading");
  private componentRef?: ComponentRef<unknown>;
  private mountedLabel = "";
  private focusRequested = false;
  private focusSection?: string;

  get activeTabIndex(): number { return this.selected(); }
  set activeTabIndex(index: number) { this.selected.set(index); }

  activeLabel(): string { return read(this.tabConfig()[this.selected()]?.label) ?? ""; }

  constructor() {
    effect(() => {
      const phase = this.hearingPhase();
      if (phase === null || this.navigation.context()?.phase !== phase) return;
      const section = this.navigation.section();
      const sections = HEARING_NAVIGATION.find(group => group.view === phase)?.sections;
      const index = sections?.findIndex(task => task.id === section) ?? -1;
      const task = this.tabConfig()[index];
      if (index < 0 || !task || read(task.hidden) || read(task.disabled)) return;
      untracked(() => {
        if (this.selected() !== index) this.selected.set(index);
        this.taskSelected.emit(index);
      });
    });
    effect(() => {
      const tasks = this.tabConfig();
      let index = this.selected();
      if (!tasks[index] || read(tasks[index].hidden) || read(tasks[index].disabled)) {
        index = tasks.findIndex(task => !read(task.hidden) && !read(task.disabled));
        this.selected.set(index);
      }
      const task = tasks[index];
      const container = this.content();
      if (!container) return;
      const label = task ? read(task.label) ?? "" : "";
      untracked(() => {
        if (!task?.component) {
          container.clear();
          this.componentRef = undefined;
          return;
        }
        if (!this.componentRef || this.componentRef.componentType !== task.component || this.mountedLabel !== label) {
          container.clear();
          const ref = container.createComponent(task.component);
          this.componentRef = ref;
          this.mountedLabel = label;
          for (const eventName of Object.keys(task.events ?? {})) {
            const emitter = (ref.instance as Record<string, unknown>)[eventName];
            if (emitter instanceof EventEmitter) {
              const sub = emitter.subscribe(value => this.tabConfig()[this.selected()]?.events?.[eventName]?.(value));
              ref.onDestroy(() => sub.unsubscribe());
            }
          }
        }
        for (const [name, value] of Object.entries(task.inputs ?? {})) {
          this.componentRef.setInput(name, value);
        }
        if (this.focusRequested) {
          this.focusRequested = false;
          const section = this.focusSection;
          this.focusSection = undefined;
          queueMicrotask(() => {
            if (this.selected() !== index) return;
            const root = this.componentRef?.location.nativeElement as HTMLElement | undefined;
            const target = section ? Array.from(root?.querySelectorAll<HTMLElement>("[data-review-target]") ?? [])
              .find(element => element.dataset["reviewTarget"] === section) : undefined;
            if (target) {
              const control = target.querySelector<HTMLElement>("input:not([disabled]), textarea:not([disabled]), select:not([disabled])") ?? target;
              control.focus({ preventScroll: true });
              control.scrollIntoView?.({ block: "start" });
            } else {
              this.heading()?.nativeElement.focus({ preventScroll: true });
            }
          });
        }
      });
    });
  }

  /** Selects a task; on a hearing phase the selection is routed through the hearing navigation so the page's tab follows. */
  onTabChange(index: number, section?: string): void {
    const task = this.tabConfig()[index];
    if (!task || read(task.hidden) || read(task.disabled)) return;
    this.focusRequested = true;
    this.focusSection = section;
    this.selected.set(index);
    this.taskSelected.emit(index);
    const phase = this.hearingPhase();
    if (phase !== null) {
      const id = HEARING_NAVIGATION.find(group => group.view === phase)?.sections[index]?.id;
      if (id) this.navigation.selectSection(id);
    }
  }
}
